import {
  FluidFieldCustomValidation,
  FluidFieldValidation,
  FluidFieldValidationType,
  FluidFormArrayItem,
  FluidFormElementConfig,
  FluidFormElementOutputType,
  FluidFormElementState,
  FluidFormState,
  FluidInputType,
  FluidKeyedObject,
  FluidOptGroupConfig,
  FluidOption,
  FluidOutputFormatConfig,
  FluidSortOrder,
  FluidUpdateStrategy,
  isNullOrUndefined,
} from '@lmig/fluid-core';

import { sendEvent } from '../../components/fluid-data-layer/fluid-data-layer.utils';
import { getActiveForms } from '../../components/fluid-form-array/utils/fluid-form-array.utils';
import { NESTED_FORM_HAS_ERRORS } from '../../constants/constants';
import { DELETE_INPUT, EventUtils } from '../events/event-utils';
import { Utils } from '../utils';
import { validate } from '../validation/validation.utils';
import { canBeCoercedToNumber, setInitialElementState } from './form-utils';

export const hasMethod = (host, method) => typeof host[method] === 'function';

/**
 * Checks whether a debounced delete should be used on delete keypress or not.
 * @param keyPress
 * @param debouncedDeleteTime
 */
export function isDebouncedDelete(
  keyPress: { inputType: string },
  debouncedDeleteTime: number
): boolean {
  return (
    !!debouncedDeleteTime &&
    debouncedDeleteTime !== -1 &&
    keyPress.inputType === DELETE_INPUT
  );
}

export type FormElementValueChange = {
  dataPath: string;
  changed: boolean;
  value: FluidFormElementState;
};
export async function setValue(
  host,
  value,
  onValueChanged?: () => void,
  opts?: Record<string, any>
): Promise<FormElementValueChange> {
  const valueHasChanged: FormElementValueChange = {
    dataPath: host.config?.dataPath || host.controlName,
    changed: host._elementState.value !== value,
    value: await host.getValue(),
  };
  if (!isNullOrUndefined(value)) {
    host.touched = opts?.preserveTouched || false;
    host._elementState = {
      ...host._elementState,
      value,
      touched: host.touched,
    };

    await host.validate();

    if (onValueChanged) {
      onValueChanged();
    }
  } else {
    await host.reset(true);
  }

  return valueHasChanged;
}

export const errorAlreadyExists = (
  error: FluidFieldValidation,
  elementState: FluidFormElementState
) => {
  return (
    elementState.errors.findIndex(
      (e: FluidFieldValidation) => e.type === error.type
    ) !== -1
  );
};

export async function validateEntries(
  host: HTMLFluidFormArrayElement,
  elementState: FluidFormElementState,
  forms: FluidFormArrayItem[],
  customValidators: FluidFieldCustomValidation[],
  parentState: FluidFormState,
  externalState: Record<string, any> = {}
) {
  const minEntries: FluidFieldValidation = host.config?.validation?.find(
    (v) => v.type === FluidFieldValidationType.MIN_ENTRIES
  );
  const activeEntries = getActiveForms(forms);
  elementState = validateMinEntries(minEntries, elementState);

  const { config } = host ?? {};

  const { label, errorConfig } = config ?? {};

  const { message, disabled } = errorConfig ?? {};

  elementState = validateEntriesAreValid(
    activeEntries,
    elementState,
    label,
    message,
    disabled
  );
  elementState = runCustomValidations(
    elementState,
    customValidators,
    activeEntries,
    parentState,
    externalState
  );

  return {
    ...elementState,
    valid: elementState.errors?.length === 0,
  };
}

export function runCustomValidations(
  elementState: FluidFormElementState,
  customValidators: FluidFieldCustomValidation[] = [],
  activeEntries: FluidFormArrayItem[],
  parentState: Record<string, any> = {},
  externalState: Record<string, any> = {}
) {
  return customValidators.reduce(
    (
      newElementState: FluidFormElementState,
      validator: FluidFieldCustomValidation
    ) => {
      const failsValidator = (validator: FluidFieldCustomValidation): boolean =>
        !validator.validatorFn(
          activeEntries.map((entry) => entry.state.redactedValue),
          parentState,
          externalState
        );
      if (
        failsValidator(validator) &&
        !errorAlreadyExists(validator, elementState)
      ) {
        newElementState.errors.push(validator);
      }
      return newElementState;
    },
    elementState
  );
}

/**
 * Validates that the minimum entry validation has been met (if applicable)
 * @param minEntries
 * @param elementState
 */
export function validateMinEntries(
  minEntries: FluidFieldValidation,
  elementState: FluidFormElementState
): FluidFormElementState {
  const valueLength = elementState.value?.length;
  if (
    minEntries &&
    valueLength < minEntries.value &&
    !errorAlreadyExists(minEntries, elementState)
  ) {
    elementState.errors.push(minEntries);
  }
  return elementState;
}

/**
 * Adds an error to the errors array if any of the active entries are invalid.
 * @param formEntries
 * @param elementState
 * @param label
 * @param invalidEntryMessage
 * @param disabled
 */
export function validateEntriesAreValid(
  formEntries: FluidFormArrayItem[],
  elementState: FluidFormElementState,
  label: string,
  invalidEntryMessage: (label: string) => string,
  disabled: boolean
): FluidFormElementState {
  const invalidEntries: FluidFieldValidation = {
    type: FluidFieldValidationType.INVALID_ENTRIES,
    value: formEntries.reduce(
      (errors: any[], currentEntry: FluidFormArrayItem) => {
        return [
          ...errors,
          { entry: currentEntry.activeIdx, errors: currentEntry.state.errors },
        ];
      },
      []
    ),
    message: disabled
      ? ''
      : invalidEntryMessage
      ? invalidEntryMessage(label)
      : NESTED_FORM_HAS_ERRORS(label),
  };
  const containsInvalid = !formEntries
    .map((f) => f.state.valid)
    .every((isValid) => isValid);
  if (containsInvalid && !errorAlreadyExists(invalidEntries, elementState)) {
    elementState.errors.push(invalidEntries);
  }
  return elementState;
}

export async function getValue(host) {
  await host.validate();
  return {
    ...handleNumberCoercion(host._elementState, host.type, host.outputType),
    dataPath: host.config?.dataPath || host.controlName,
  };
}

export type FluidFormElementValidationResult = {
  name: string;
  errors: string[];
  valid: boolean;
};
export async function validateElement(
  host
): Promise<FluidFormElementValidationResult> {
  const errors: string[] = validate(
    host._elementState,
    host.validation,
    host._parentState,
    host.externalState
  );
  host._elementState = {
    ...host._elementState,
    errors,
    valid: errors.length === 0,
  };
  return {
    name: host.controlName || host.dataPath,
    errors,
    valid: (errors || []).length === 0,
  };
}

export async function reset(host, clear = false, onReset?: Function) {
  host._elementState = setInitialElementState(
    clear ? '' : host.initialValue,
    host.controlName
  );
  host.touched = false;
  if (onReset) {
    onReset();
  }
  // prevent infinite loop when the value is an empty string
  if (!host._standalone && host._elementState.value) {
    host.valueChange.emit(applyElementOutputFormatting(host));
  }
  return host._elementState;
}

export async function markTouchedAndValidate(host) {
  host.touched = true;
  host._elementState = { ...host._elementState, touched: host.touched };
  return await validateElement(host);
}

/**
 * Updates a form elements value and emits its element state upwards
 * via its valueChange event emitter.
 * @param host
 * @param event
 * @param onValidate
 * @param emitValueChange
 * @param valueChangeEmitter
 */
export function update(
  host,
  event,
  onValidate?: Function,
  emitValueChange = true,
  valueChangeEmitter = () => emitValueChangeEvent(host)
) {
  // If element value matches element value, no need to update
  // (short-circuit for 'blur' update strategy)
  if (event.target.value === host._elementState.value) return;

  let value: unknown = event.target.value;

  if (shouldApplyInlineFormatting(host.config, host.updateStrategy)) {
    value = host.config.outputFormat.formatter(value);
  }

  host.touched = true;
  host._elementState = {
    ...host._elementState,
    value,
    touched: host.touched,
  };
  host.validate().then(() => {
    if (onValidate) {
      onValidate();
    }
    if (emitValueChange) {
      valueChangeEmitter();
    }
  });
}

export function emitValueChangeEvent(host: any): void {
  Utils.debug('[FORM] Value change emitted', host, host?._elementState);
  sendEvent(host, host.parentLayerId, 'valueChange', host._elementState);
  host.valueChange.emit(applyElementOutputFormatting(host));
}

/**
 * If the element is standalone (i.e not part of a form) and has an output format
 * configured, apply it before emitting on valueChange
 * @param host
 */
export const applyElementOutputFormatting = (host): FluidFormElementState => {
  const {
    _standalone,
    outputFormat,
    _elementState,
  }: {
    _standalone: boolean;
    outputFormat: FluidOutputFormatConfig;
    _elementState: FluidFormElementState;
  } = host;

  if (!_standalone || !outputFormat || !outputFormat.formatter) {
    return _elementState;
  }
  return {
    ..._elementState,
    value: outputFormat.formatter(_elementState.value),
  };
};

export function handleNumberCoercion(
  _elementState: FluidFormElementState,
  type: FluidInputType,
  outputType: FluidFormElementOutputType
) {
  if (
    shouldCoerceToNumber(type, outputType) &&
    canBeCoercedToNumber(_elementState.value)
  ) {
    _elementState.value = !Utils.isNullOrUndefined(_elementState.value, true)
      ? Number(_elementState.value)
      : null;
  }
  return _elementState;
}

/**
 * Check if the given element type is a number or currency input and that outputFormat is configured as a number.
 * @param type
 * @param outputType
 */
export const shouldCoerceToNumber = (
  type: FluidInputType,
  outputType: FluidFormElementOutputType
) => {
  return (
    (type === FluidInputType.NUMBER || type === FluidInputType.CURRENCY) &&
    outputType === FluidFormElementOutputType.NUMBER
  );
};

export const shouldApplyInlineFormatting = (
  config: FluidFormElementConfig,
  updateStrategy: FluidUpdateStrategy
) => {
  return (
    config &&
    config?.outputFormat &&
    config?.outputFormat?.applyInline &&
    updateStrategy?.toUpperCase() === FluidUpdateStrategy.BLUR
  );
};

/**
 * If form element is configured via a single config object, map
 * all those values to the local element.
 *
 * @param host - the host element to carry out the mapping on
 * @param selective - if true, will only remap properties that are different
 * (prevents unnecessary re-renders)
 */
export function mapConfigToLocal(host, selective = false) {
  if (host.config) {
    Object.keys(host.config).forEach((key) => {
      if (selective) {
        if (host[key] !== host.config[key]) {
          host[key] = host.config[key];
        }
      } else {
        host[key] = host.config[key];
      }
    });
  }
}

export function toControlName(value: string) {
  if (isNullOrUndefined(value)) return;
  return String(value)
    .replace(/^\w|[A-Z]|\b\w/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
}

/**
 * Returns true if any of the options have a grouping configuration.
 * NOTE: "group" property MUST at least have the "label" property set
 * to be considered a valid grouping configuration.
 * @param options - FluidOption array to check for groups
 */
export const hasGroupedOptions = (options: FluidOption[]): boolean =>
  !!options?.some((o) => !!o.group?.label);

/**
 * Extracts group label strings from array of FluidOptions. Labels
 * are returned in the order in which they occur in the "options" array
 * UNLESS the "sortOrder" parameter is provided - if so, the labels will
 * be sorted ascending or descending.
 * @param options - array of grouped FluidOptions
 * @param sortOrder - order in which to sort extracted group label strings
 */
export const getOptGroupLabels = (
  options: FluidOption[],
  sortOrder?: FluidSortOrder
): string[] => {
  const groupLabels = gatherGroupLabels(options);
  return sortOrder
    ? sortOrder === FluidSortOrder.ASC
      ? groupLabels.sort()
      : groupLabels.sort().reverse()
    : groupLabels;
};

/**
 * Pulls the group labels out of the options as strings in the default order
 * they are passed in.
 * @param options
 */
export const gatherGroupLabels = (options?: FluidOption[]) => {
  return options.reduce((labels: string[], opt: FluidOption) => {
    const { group } = opt;
    return !!group?.label && !labels.includes(group.label)
      ? [...labels, group.label]
      : labels;
  }, []);
};

/**
 * Groups a set of FluidOptions by their group key/label.
 * @param options
 */
type TranslatableOptGroup = { groupId } & FluidOptGroupConfig;
export type TranslatedGroup = {
  groupId: string;
  label: string;
  options: FluidOption[];
};
export const groupOptions = (options: FluidOption[]) =>
  options.reduce(
    (groups: FluidKeyedObject<TranslatedGroup>, option: FluidOption) => {
      if (option.group) {
        const { label, groupId } = option.group as TranslatableOptGroup;
        const identifier: string = groupId ? groupId : label;
        if (groups[identifier]) {
          groups[identifier].options.push(option);
        } else {
          groups[identifier] = {
            groupId: identifier,
            label: option.group.label,
            options: [option],
          };
        }
      }
      return groups;
    },
    {}
  );

export const handleAutoFocus = (host) => {
  if (host._autoFocus && host._elementRef !== Utils.getActiveElement()) {
    host._elementRef.focus();
  }
  if (host._autoFocus && host._elementRef === Utils.getActiveElement()) {
    host._autoFocus = false;
  }
};

/**
 * Prevents form field <input> from losing :focus when other child elements are
 * clicked (e.g. dropdown menu "trigger" button, "cancelInput" button, etc.).
 */
export const resetFocus = (host, e: MouseEvent): void => {
  if (
    EventUtils.eventPathIncludes(e, host._elementRef) ||
    host.disabled ||
    host.readonly
  )
    return;
  e.preventDefault();
  e.stopPropagation();
  host._elementRef.focus();
};

/**
 * Returns true if "_elementState.value" contains valid value, & returns
 * false if value is undefined, empty string, or empty array (NOTE: the
 * number "0" IS considered a valid value).
 * @param val - a form field's "_elementState.value"
 */
export const hasValue = (val: any): boolean => {
  switch (true) {
    case isNullOrUndefined(val, true):
      return false;
    case Array.isArray(val) && !val.length:
      return false;
    case val === '':
      return false;
    default:
      return true;
  }
};

export const getFormInputId = (id: string, alternativeId: string): string => {
  return id || alternativeId;
};

/**
 * Validates shorthand number inputs to prevent incorrect data entry
 * @param keyDownEvent - event that's fired on keyboard input, used to prevent an invalid shorthand number entry
 */
export const preventInvalidShortHandInput = (
  keyDownEvent: KeyboardEvent
): void => {
  const regex = /^[0-9]*\.?[0-9]*?[KMBbkm]?$/;
  const specialKeys = new Set([
    'Backspace',
    'Tab',
    'End',
    'Home',
    'ArrowLeft',
    'ArrowRight',
    'Del',
    'Delete',
    'Control',
    'Meta',
  ]);

  // Special characters that are allowed when Ctrl/Meta is pressed
  // (e.g. Ctrl + v, Ctrl + c, Ctrl + x, Ctrl + a, Ctrl + z)
  const allowedCtrlMetaChars = new Set(['v', 'c', 'x', 'a', 'z']);

  // Allow special keys or Ctrl/Meta + special characters
  if (
    specialKeys.has(keyDownEvent.key) ||
    ((keyDownEvent.ctrlKey || keyDownEvent.metaKey) &&
      allowedCtrlMetaChars.has(keyDownEvent.key))
  ) {
    return;
  }

  const target = keyDownEvent.target as HTMLInputElement;
  const currentValue = target.value || '';
  const cursorPosition = target.selectionStart || 0;

  // Construct the next value based on the key pressed
  const nextValue = [
    currentValue.slice(0, cursorPosition),
    keyDownEvent.key === 'Decimal' ? '.' : keyDownEvent.key,
    currentValue.slice(cursorPosition),
  ].join('');

  // Validate the next value
  if (
    !regex.test(nextValue) ||
    ['m', 'k', 'b'].includes(nextValue.toLowerCase())
  ) {
    keyDownEvent.preventDefault();
  }
};
